import '@mdxeditor/editor/style.css'

import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import CloseIcon from 'components/ReusableComponents/CloseIcon'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import ContentIcon from 'components/ReusableComponents/Icons/Content'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { ColorInput, ImageFileInput } from 'features/ConfigureWidgets'
import {
  editableSectionNames,
  propertyNames,
  propertyTypes,
  sectionsToProperties,
  DESIGN_LOCKED_PROPERTIES,
  CAMPAIGN_BODY_EDITABLE_PROPERTIES,
  CAMPAIGN_INBOX_EDITABLE_PROPERTIES
} from 'features/EmailEditor/constants'
import { useEmailEditor } from 'features/EmailEditor/EmailEditorContext'
import { resolveEditorRichtextFields } from 'features/EmailEditor/campaignEmailCustomizationHelpers'
import { EmailInboxPreview } from 'features/EmailEditor/EmailPreview'
import RichTextEditor from 'features/EmailEditor/RichTextEditor'
import EmailTemplatePicker from 'features/EmailEditor/EmailTemplatePicker'
import { getEmailImageUploadConfig } from 'features/EmailEditor/emailImageUploadHelpers'
import { getTemplateHeroFields } from 'features/EmailEditor/templateHeroFields'
import {
  getTemplatePropertyLabel,
  getTemplateSectionLabel,
  getTemplateSectionProperties
} from 'features/EmailEditor/templateSectionProperties'
import { useEffect, useMemo, useState } from 'react'
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark'

import styles from './EmailEditor.module.scss'

const PanelInner = ({
  onClose,
  onDiscard,
  setEditingSection,
  editingSection,
  sections,
  sectionControls,
  handleSectionChange,
  hideFooter,
  selectedTemplate
}) => {
  const { hasUnsavedChanges } = useEmailEditor()

  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)

  function handleClosePanel() {
    setIsDiscardDialogOpen(false)
    setEditingSection('inbox-preview')
  }

  function handleClickDiscard() {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true)
    } else {
      handleClosePanel()
    }
  }

  return (
    <div className={styles.innerDialog}>
      {isDiscardDialogOpen && (
        <ConfirmationDialog
          title="Discard Changes?"
          positiveMessage="Discard"
          negativeMessage="Go back"
          hasDangerousAction={true}
          onClose={() => {
            setIsDiscardDialogOpen(false)
          }}
          onConfirm={() => {
            onDiscard()
            setIsDiscardDialogOpen(false)
            handleClosePanel()
          }}
        >
          <p style={{ margin: 0 }}>Are you sure you want to discard your changes?</p>
        </ConfirmationDialog>
      )}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.headerTitle}>Editing email</span>
          <button className={styles.closeButton} onClick={handleClosePanel}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.tabsHolder}>
          <div className={styles.tabs}>
            {sections.map(section => (
              <button
                key={section}
                onClick={() => handleSectionChange(section)}
                className={section === editingSection ? styles.activeTab : styles.tab}
              >
                <span className={styles.paginatorNo}>
                  {getTemplateSectionLabel(selectedTemplate, section)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.inputsHeader}>
        <p className={style.sectionTitle}>
          {getTemplateSectionLabel(selectedTemplate, editingSection)}
        </p>
      </div>
      <div className={styles.controlsHolder}>{sectionControls()}</div>
      {!hideFooter && (
        <div className={styles.footer}>
          <SecondaryRegularButton onClick={handleClickDiscard}>
            {hasUnsavedChanges ? 'Discard changes' : 'Go back'}
          </SecondaryRegularButton>
          <PrimaryRegularButton onClick={handleClosePanel}>Close</PrimaryRegularButton>
        </div>
      )}
    </div>
  )
}

const EmailEditorPanel = ({
  onClose,
  hideFooter,
  embedded = false,
  campaignBodyOnly = false,
  includeInboxFields = false,
  compact = false
}) => {
  const {
    editingSection,
    setEditingSection,
    selectedTemplate,
    emailState,
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    sections,
    isDesignMode,
    editorHydrationKey,
    currentTemplate,
    resolveEditorImageField
  } = useEmailEditor()

  const resolvedRichtextFields = resolveEditorRichtextFields(
    currentTemplate.params,
    emailState.richtextFields || {}
  )

  const hasChanges = true

  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)

  const contentSections = useMemo(
    () => sections.filter(section => section !== 'inbox-preview'),
    [sections]
  )
  const activeEditingSection =
    embedded && (!editingSection || editingSection === 'inbox-preview')
      ? contentSections[0] || 'body'
      : editingSection

  function handleClickClose() {
    if (hasChanges) {
      setIsDiscardDialogOpen(true)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if ((embedded || campaignBodyOnly) && editingSection === 'inbox-preview') {
      setEditingSection(campaignBodyOnly ? null : 'body')
    }
  }, [embedded, campaignBodyOnly, editingSection, setEditingSection])

  // Function to scroll to a section in the preview
  const scrollToSection = sectionName => {
    const sectionClass = sectionName.startsWith('mjml-') ? sectionName : `mjml-${sectionName}`
    const targets = document.querySelectorAll(`.${sectionClass}`)
    const targetElement = targets.length ? targets[targets.length - 1] : null

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })

      // Add highlight effect
      document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
        el.classList.remove(styles.highlightedSection)
      })
      targetElement.classList.add(styles.highlightedSection)
    }
  }

  // Enhanced setEditingSection that includes scrolling
  const handleSectionChange = sectionName => {
    setEditingSection(sectionName)
    if (sectionName !== 'inbox-preview') {
      // Small delay to ensure the preview is rendered before scrolling
      setTimeout(() => scrollToSection(sectionName), 100)
    }
  }

  useEffect(() => {
    if (campaignBodyOnly) {
      return
    }

    if (!editingSection || editingSection === 'inbox-preview') {
      scrollToSection('header')
    }
  }, [editingSection, campaignBodyOnly])

  function renderPropertyField(property) {
    if (isDesignMode && DESIGN_LOCKED_PROPERTIES.includes(property)) {
      return null
    }

    const fieldLabel = getTemplatePropertyLabel(selectedTemplate, property)
    const type = propertyTypes[property]
    if (type === 'color') {
      return (
        <div key={property} className={styles.inputGroup}>
          <ColorInput
            title={fieldLabel}
            height={32}
            defaultColor={emailState[property]}
            onChange={value => updateEmailState(property, value)}
          />
        </div>
      )
    } else if (type === 'socialmedia') {
      return (
        <div key={property} className={styles.inputGroup}>
          <span className={styles.socialMediaLabel}>{fieldLabel}</span>
          {emailState.socialMediaLinks.map((link, index) => (
            <TextInput
              key={index}
              label={link.platform}
              value={link.url}
              onChange={e => handleSocialMediaChange(index, 'url', e.target.value)}
            />
          ))}
        </div>
      )
    } else if (type === 'image') {
      const uploadConfig = getEmailImageUploadConfig(property)

      return (
        <ImageFileInput
          key={property}
          kind={uploadConfig.kind}
          title={fieldLabel}
          backgroundSize={property.includes('logo') ? '90%' : 'cover'}
          defaultUrl={resolveEditorImageField(property) || ''}
          onChange={text => {
            updateEmailState(property, text)
          }}
          minWidth={uploadConfig.minWidth}
          minHeight={uploadConfig.minHeight}
        />
      )
    } else if (type === 'richtext') {
      return (
        <div key={property} className={styles.inputGroup}>
          <RichTextEditor
            key={`${property}-${selectedTemplate}-${editorHydrationKey}-${activeEditingSection}`}
            editorKey={`${property}-${selectedTemplate}-${editorHydrationKey}-${activeEditingSection}`}
            label={fieldLabel}
            value={resolvedRichtextFields[property] || ''}
            onChange={value => updateRichTextField(property, value)}
          />
        </div>
      )
    } else if (type === 'multilinetext') {
      return (
        <div key={property} className={styles.inputGroup}>
          <TextArea
            label={fieldLabel}
            value={emailState.preheaderText || ''}
            onChangeValue={value => updateEmailState(property, value)}
            multiline={true}
          />
        </div>
      )
    }

    return (
      <div key={property} className={styles.inputGroup}>
        <TextInput
          label={fieldLabel}
          value={emailState[property]}
          onChangeValue={text => {
            updateEmailState(property, text)
          }}
        />
      </div>
    )
  }

  const getSectionProperties = sectionKey => {
    const properties =
      sectionKey === 'hero'
        ? getTemplateHeroFields(selectedTemplate)
        : getTemplateSectionProperties(selectedTemplate, sectionKey)

    return properties.filter(
      property => !(isDesignMode && DESIGN_LOCKED_PROPERTIES.includes(property))
    )
  }

  const activeSectionLabel = getTemplateSectionLabel(selectedTemplate, activeEditingSection)

  const sectionControls = () => (
    <>
      {getSectionProperties(activeEditingSection).map(property =>
        renderPropertyField(property)
      )}
    </>
  )

  const templatePicker = (
    <EmailTemplatePicker
      selectedTemplate={selectedTemplate}
      onTemplateChange={handleTemplateChange}
    />
  )

  if (campaignBodyOnly) {
    const customizationProperties = [
      ...(includeInboxFields ? CAMPAIGN_INBOX_EDITABLE_PROPERTIES : []),
      ...CAMPAIGN_BODY_EDITABLE_PROPERTIES
    ]

    return (
      <div className={styles.campaignBodyEditor}>
        <div
          className={
            compact
              ? `${styles.campaignBodyControls} ${styles.campaignBodyControlsCompact}`
              : styles.campaignBodyControls
          }
        >
          {customizationProperties.map(property => renderPropertyField(property))}
          {includeInboxFields && (
            <div className={styles.inboxPreviewField}>
              <label className={styles.fieldLabel}>Inbox preview</label>
              <EmailInboxPreview />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (embedded) {
    return (
      <div className={styles.embeddedPanel}>
        <div className={styles.embeddedSettings}>
          {templatePicker}
          {sectionsToProperties['inbox-preview'].map(property => renderPropertyField(property))}
          <div className={styles.inboxPreviewField}>
            <label className={styles.fieldLabel}>Inbox preview</label>
            <EmailInboxPreview />
          </div>
        </div>
        <div className={styles.embeddedSectionEditor}>
          <div className={styles.embeddedHeader}>
            <div className={styles.tabsHolder}>
              <div className={styles.tabs}>
                {contentSections.map(section => (
                  <button
                    key={section}
                    type="button"
                    onClick={() => handleSectionChange(section)}
                    className={section === activeEditingSection ? styles.activeTab : styles.tab}
                  >
                    <span className={styles.paginatorNo}>
                      {getTemplateSectionLabel(selectedTemplate, section)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.inputsHeader}>
            <p className={styles.sectionTitle}>{activeSectionLabel}</p>
          </div>
          <div className={styles.embeddedControls}>{sectionControls()}</div>
        </div>
      </div>
    )
  }

  if (editingSection === 'inbox-preview') {
    return (
      <div className={styles.inputs}>
        {editingSection && editingSection !== 'inbox-preview' && (
          <div className={styles.inputsHeader}>
            <p className={style.sectionTitle}>{editableSectionNames[editingSection]}</p>
          </div>
        )}
        <div className={styles.inputsInner}>
          {editingSection === 'inbox-preview' && templatePicker}

          {/* Email Editor Inputs according to the active section */}
          {sectionControls()}
          {editingSection === 'inbox-preview' && (
            <div className={styles.inboxPreviewField}>
              <label className={styles.fieldLabel}>Inbox preview</label>
              <EmailInboxPreview />
            </div>
          )}
        </div>
      </div>
    )
  } else if (editingSection) {
    return (
      <PanelInner
        isDiscardDialogOpen={isDiscardDialogOpen}
        setIsDiscardDialogOpen={setIsDiscardDialogOpen}
        handleClickClose={handleClickClose}
        onDiscard={onClose}
        setEditingSection={setEditingSection}
        editingSection={editingSection}
        sections={sections}
        sectionControls={sectionControls}
        handleSectionChange={handleSectionChange}
        hideFooter={hideFooter}
        selectedTemplate={selectedTemplate}
      />
    )
  }
}

export default EmailEditorPanel
